/**
 * @shared/utils/scroll.ts
 * 스크롤 관련 유틸리티 함수 모음
 *
 * 주요 기능:
 * - 요소를 화면 중앙으로 부드럽게 스크롤
 * - 스크롤 가능한 부모 컨테이너 찾기
 * - 특정 영역 내 스크롤 가능 여부 확인
 */

// ============================================
// 📌 Type Definitions
// ============================================

/**
 * 스크롤 동작 옵션
 */
export interface ScrollIntoViewOptions {
	/** 스크롤 애니메이션 방식 (기본: 'smooth') */
	behavior?: ScrollBehavior;
	/** 수직 정렬 위치 (기본: 'center') */
	block?: ScrollLogicalPosition;
	/** 수평 정렬 위치 (기본: 'nearest') */
	inline?: ScrollLogicalPosition;
}

/**
 * 스크롤 가능 여부 정보
 */
export interface ScrollableInfo {
	/** Y축 스크롤 가능 여부 */
	canScrollY: boolean;
	/** X축 스크롤 가능 여부 */
	canScrollX: boolean;
	/** 하나라도 스크롤 가능한지 */
	isScrollable: boolean;
}

// ============================================
// 📌 Constants
// ============================================

/**
 * 스크롤 가능한 CSS overflow 값들
 */
const SCROLLABLE_OVERFLOW_VALUES = ['auto', 'scroll'] as const;

/**
 * 기본 스크롤 옵션
 * - 부드러운 애니메이션으로 화면 중앙에 배치
 */
const DEFAULT_SCROLL_OPTIONS: Required<ScrollIntoViewOptions> = {
	behavior: 'smooth',
	block: 'center',
	inline: 'nearest',
} as const;

// ============================================
// 📌 Low-level Utilities
// ============================================

/**
 * 요소의 CSS overflow 속성이 스크롤 가능한지 확인
 *
 * @param element - 확인할 HTML 요소
 * @returns Y축과 X축의 overflow 스크롤 가능 여부
 *
 * @example
 * ```ts
 * const div = document.querySelector('.container');
 * const overflow = hasScrollableOverflow(div);
 * console.log(overflow.y); // true if overflow-y is 'auto' or 'scroll'
 * ```
 */
export const hasScrollableOverflow = (
	element: HTMLElement
): { y: boolean; x: boolean } => {
	const style = window.getComputedStyle(element);

	return {
		y: SCROLLABLE_OVERFLOW_VALUES.includes(style.overflowY as any),
		x: SCROLLABLE_OVERFLOW_VALUES.includes(style.overflowX as any),
	};
};

/**
 * 요소가 실제로 스크롤 가능한 콘텐츠를 가지고 있는지 확인
 *
 * CSS overflow 속성만 체크하는 것이 아니라,
 * 실제로 콘텐츠가 넘쳐서 스크롤이 발생하는지 확인
 *
 * @param element - 확인할 HTML 요소
 * @returns 스크롤 가능 여부 상세 정보
 *
 * @example
 * ```ts
 * const container = document.querySelector('.list-container');
 * const info = getScrollableInfo(container);
 *
 * if (info.canScrollY) {
 *   console.log('세로 스크롤 가능');
 * }
 * ```
 */
export const getScrollableInfo = (element: HTMLElement): ScrollableInfo => {
	const overflow = hasScrollableOverflow(element);

	// CSS overflow 속성 + 실제 콘텐츠 overflow 발생 여부
	const canScrollY = overflow.y && element.scrollHeight > element.clientHeight;
	const canScrollX = overflow.x && element.scrollWidth > element.clientWidth;

	return {
		canScrollY,
		canScrollX,
		isScrollable: canScrollY || canScrollX,
	};
};

// ============================================
// 📌 Core Functions
// ============================================

/**
 * DOM 트리를 거슬러 올라가며 스크롤 가능한 가장 가까운 부모 요소를 찾음
 *
 * 본인은 제외하고 부모부터 시작 (요소 자신이 아닌 컨테이너를 찾기 위함)
 *
 * @param element - 시작 지점 HTML 요소
 * @returns 스크롤 가능한 부모 요소 (없으면 undefined)
 *
 * @example
 * ```ts
 * const input = document.querySelector('input');
 * const scrollContainer = findScrollableAncestor(input);
 *
 * if (scrollContainer) {
 *   console.log('스크롤 컨테이너:', scrollContainer);
 * }
 * ```
 */
export const findScrollableAncestor = (
	element: HTMLElement | null
): HTMLElement | undefined => {
	// null 체크 + DOM 연결 상태 확인
	if (!element?.isConnected) return undefined;

	let current = element.parentElement;

	while (current) {
		const info = getScrollableInfo(current);

		if (info.isScrollable) {
			return current;
		}

		current = current.parentElement;
	}

	return undefined;
};

/**
 * 특정 root 요소 안에 스크롤 가능한 컨테이너가 있는지 확인
 *
 * 주로 드롭다운에서 사용:
 * - 선택된 옵션이 드롭다운 컨테이너 내부에 있는지 확인
 * - 내부에 있으면 스크롤, 외부면 스크롤 안 함
 *
 * @param element - 확인할 요소
 * @param root - 범위를 제한할 root 요소
 * @returns root 안에 스크롤 컨테이너가 있으면 true
 *
 * @example
 * ```ts
 * // 드롭다운 옵션 스크롤 예시
 * const selectedOption = dropdown.querySelector('.selected');
 *
 * if (hasScrollContainerWithin(selectedOption, dropdown)) {
 *   // dropdown 내부에서만 스크롤
 *   smoothScrollToCenter(selectedOption);
 * }
 * ```
 */
export const hasScrollContainerWithin = (
	element: HTMLElement | null,
	root: HTMLElement | null
): boolean => {
	if (!element?.isConnected || !root?.isConnected) return false;

	const scrollContainer = findScrollableAncestor(element);

	if (!scrollContainer) return false;

	return root.contains(scrollContainer);
};

/**
 * 요소를 부드럽게 화면 중앙으로 스크롤
 *
 * 주요 사용처:
 * 1. Input/Checkbox onFocus - 모바일 키보드가 올라와도 요소가 가려지지 않게
 * 2. 드롭다운 - 선택된 옵션을 보이는 영역으로 이동
 *
 * 안전성:
 * - null/undefined 요소 무시
 * - DOM에서 제거된 요소 무시
 * - 옵션 커스터마이징 가능
 *
 * @param element - 스크롤할 HTML 요소
 * @param options - 스크롤 동작 옵션 (기본: 부드럽게 중앙으로)
 *
 * @example
 * ```ts
 * // 기본 사용 (중앙으로 부드럽게)
 * smoothScrollToCenter(inputElement);
 *
 * // onFocus 이벤트에서
 * <input
 *   onFocus={(e) => smoothScrollToCenter(e.currentTarget)}
 * />
 *
 * // 커스텀 옵션 (맨 위로 즉시)
 * smoothScrollToCenter(errorElement, {
 *   block: 'start',
 *   behavior: 'auto'
 * });
 * ```
 */
export const smoothScrollToCenter = (
	element: HTMLElement | null,
	options: ScrollIntoViewOptions = {}
): void => {
	// null 체크 + DOM 연결 상태 확인
	if (!element?.isConnected) return;

	element.scrollIntoView({
		...DEFAULT_SCROLL_OPTIONS,
		...options,
	});
};

// ============================================
// 📌 Legacy Compatibility
// ============================================

/**
 * @deprecated Use `smoothScrollToCenter` instead
 * 레거시 코드 호환성을 위한 별칭
 */
export const scrollElementIntoView = smoothScrollToCenter;

/**
 * @deprecated Use `findScrollableAncestor` instead
 * 레거시 코드 호환성을 위한 별칭
 */
export const getClosestScrollContainer = (element: Element) => {
	return findScrollableAncestor(element as HTMLElement);
};

/**
 * @deprecated Use `hasScrollContainerWithin` instead
 * 레거시 코드 호환성을 위한 별칭
 */
export const isScrollInRoot = (inputElement: Element, root: Element) => {
	return hasScrollContainerWithin(
		inputElement as HTMLElement,
		root as HTMLElement
	);
};
